import QRCode from 'qrcode'
import { useCallback, useState} from 'react'
import UseDeviceStore from '../../../modules/device/store/DeviceStore';
import { IInvoiceLog, IOrderLog } from '../../../modules/orderLog/domain/dtos/IOrderLog';
import UseSocketPrinter from '../../../modules/printer/presentation/hooks/UseSocketPrinter';
import InvoicePrinterLayout from '../../../modules/printer/presentation/layoutPrinter/invoice/InvoicePrinterLayout';
import UseSessionStore from '../../../modules/session/store/SessionStore';
import { useShallow } from 'zustand/react/shallow';
import OrderLogService from '../../../services/api/orderLog/OrderLogService';
import { GetOrdersUseCase } from '../../../modules/orderLog/application/GetOrdersUseCase';
import { IOrdersData } from '../../../modules/orderLog/domain/dtos/IOrdersData';
import { useI18n } from '../../../hooks/useI18n';

const service = OrderLogService();

const UseOrderLog = () => {
    const { t } = useI18n();
    const [device, receiptPrinterSector] = UseDeviceStore(state => [state.device, state.receiptPrinterSector])
    
    const { sendBackgroundPrinter,  getElementHtml } = UseSocketPrinter();
    const session = UseSessionStore(useShallow(state => state.session));

    const [isLoadingOrders, setIsLoadingOrders] = useState(true);
    const [orders, setOrders] = useState<IOrdersData>();
    const [currentPage, setCurrentPage] = useState(1);

    const getOrdersInvoice = useCallback(async (page?: number) => {
        if (device?.id && session?.id) {
            try {
                const response = await GetOrdersUseCase(service, device.localId, {
                    equipmentId: device.id,
                    sessionId: session.id,
                    page: page ?? 1,
                    pageSize: 10,
                });

                const itemsResult: IOrderLog[] = response.Items.map((item) => {
                    let nfceData: IInvoiceLog | undefined = undefined;
                    const nfceParseData = typeof item.NfceData === "string" && item.NfceData.includes('\"id\":') ? JSON.parse(item.NfceData) : undefined;

                    if (nfceParseData && nfceParseData.id !== null) {
                        nfceData = {
                            companyName: nfceParseData.nfceParseData,
                            cnpj: nfceParseData.cnpj,
                            series: nfceParseData.serie,
                            customer: nfceParseData.cliente,
                            status: nfceParseData.situacao,
                            totalValue: nfceParseData.valorTotal,
                            items: nfceParseData.itens.map((it) => ({
                                id: it.produtoId,
                                productName: it.produtoNome,
                                quantity: it.quantidade,
                                unitPrice: it.valorUnitario,
                                discount: it.desconto,
                                ncm: it.ncm,
                            })),
                            sefaz: {
                                code: nfceParseData.sefaz.codigoStatus,
                                danfeUrl: nfceParseData.sefaz.url,
                                emissionDate: nfceParseData.sefaz.emitidoEm,
                                message: nfceParseData.sefaz.motivo,
                                protocol: nfceParseData.sefaz.protocolo,
                            },
                            number: nfceParseData.numero,
                            error: nfceParseData.erro,
                        }
                    }
                    return {
                        id: item.Id,
                        orderItems: item.Products?.map((product) => ({
                            id: product.Id,
                            name: product.Name,
                            price: product.Value,
                            quantity: product.Quantity,
                            category: "",
                            description: "",
                            productPrice: 0,
                            productId: "",
                            productType: 1,
                            imageUrl: "",
                            totalPrice: 0,
                        })),
                        createdAt: new Date(item.OrderDate),
                        operator: {
                            id: "",
                            name: "",
                            papers: [""],
                            password: "",
                            useTagPassword: false,
                            isRoot: false,
                        },
                        invoice: nfceData ? [nfceData] : undefined,
                        invoiceStatus: item.StatusNfce,
                    }
                });
                setOrders({...response, Items: itemsResult });
                setCurrentPage(page ?? 1);
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoadingOrders(false);
            }
        }
    }, [device, session?.id]);

    const generateQR = async text => {
        return await QRCode.toDataURL(text, { width: 500 })
    }


    const printInvoice = useCallback(async (order: IOrderLog) => {
        try {
            if (!device) {
                throw new Error(t('orderHistory.noDeviceConnected'))
            }
            if (!receiptPrinterSector) {
                throw new Error(t('orderHistory.noPrinterConfigured'))
            }
            if (!!order.invoice?.length) {
                // const image = await htmlToBase64(<InvoicePrinterLayout invoice={order.invoice[0]} />)

                const qrCode = await generateQR(order.invoice[0].sefaz.danfeUrl)
                const html = await getElementHtml(<InvoicePrinterLayout invoice={order.invoice[0]} qrCode={qrCode} />)
                await sendBackgroundPrinter(order.id, receiptPrinterSector?.printerName , html)
            }
        } catch (error: any) {
            throw new Error(error?.message ?? t('orderHistory.printInvoiceError'))
        }
    }, [device, getElementHtml, receiptPrinterSector, sendBackgroundPrinter, t])




    return ({ printInvoice, isLoadingOrders, currentPage, orders, getOrdersInvoice });
}

export default UseOrderLog